/**
 * CompareProvider — the viewer's "compare against another version" state as a
 * workspace-scoped service.
 *
 * The viewer can diff the live workspace against a second source (a share link's
 * `#compare`, or a file the user loads to compare). That target + the swap
 * direction used to live as App `useState` threaded through ViewerShell as a
 * `compare` prop bag. Promoting it to a provider (like Placements/Layout) lets
 * the diff surface (`DiffBoardPane`) and the nav's compare controls read ONE
 * controller, and drops the bespoke bag from App/ViewerShell.
 *
 * The diff RENDERING stays in `diff/` (DiffBoard / split / overlay / useDiff) —
 * this only owns the compare target + direction.
 */
import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SourceSlot } from "../sources/useSources.js";
import { CompareContext, type CompareController } from "./CompareContext.js";

export function CompareProvider({
  initialAgainst = null,
  initialSwapped = false,
  children,
}: {
  initialAgainst?: SourceSlot | null;
  initialSwapped?: boolean;
  children: ReactNode;
}): ReactNode {
  const [against, setAgainst] = useState<SourceSlot | null>(initialAgainst);
  const [swapped, setSwapped] = useState(initialSwapped);

  const swapCompare = useCallback(() => setSwapped((v) => !v), []);
  const clearCompare = useCallback(() => {
    setAgainst(null);
    setSwapped(false);
  }, []);

  const value = useMemo<CompareController>(
    () => ({
      against,
      swapped,
      inDiffMode: against !== null,
      setCompareAgainst: setAgainst,
      swapCompare,
      clearCompare,
    }),
    [against, swapped, swapCompare, clearCompare],
  );
  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
}
